import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  Chip,
  Tabs,
  Tab,
} from "@material-ui/core";
import {
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  Colorize,
  FileCopy,
} from "@material-ui/icons";
import ColorPicker from "../ColorPicker";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { launchWhatsAppSignup } from "../../utils/facebookSDK";
import { startInstagramLogin } from "../../utils/instagramOAuth";
import CallingSettingsSection from "./CallingSettingsSection";
import CoexistenceStatus from "./CoexistenceStatus";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";

  // Painel tonalizado: cor solida no tema claro, tint translucido no escuro.
  // Mantem a identidade de cor da secao sem criar fundo claro sob texto branco.
  const panel = (lightBg, rgb, lightBorder) => ({
    backgroundColor: isDark ? `rgba(${rgb}, 0.12)` : lightBg,
    ...(lightBorder && {
      border: `1px solid ${isDark ? `rgba(${rgb}, 0.4)` : lightBorder}`,
    }),
  });

  return {
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  colorAdorment: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
  },
  
  kanbanSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : "#f5f5f5",
    borderRadius: 4,
    ...(isDark && { border: "1px solid rgba(255, 255, 255, 0.12)" }),
  },

  cloudApiSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    ...panel("#e8f5e9", "76, 175, 80", "#4caf50"),
  },

  apiTypeSelector: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    ...panel("#f0f4ff", "0, 174, 239", "#d6e4ff"),
  },

  validationChip: {
    marginLeft: theme.spacing(1),
  },
  embeddedSignupSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    textAlign: "center",
    ...panel("#e3f2fd", "24, 119, 242", "#1877F2"),
  },
  // Mesmo tamanho do coexistenceButton: os dois botões de conexão só mudam a cor.
  embeddedSignupButton: {
    backgroundColor: "#1877F2",
    color: "#fff",
    fontWeight: "bold",
    padding: "10px 24px",
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "#166FE5",
    },
  },
  coexistenceSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    ...panel("#e8f5e9", "37, 211, 102", "#25D366"),
    "& ul": {
      margin: theme.spacing(1, 0, 2),
      paddingLeft: theme.spacing(2.5),
    },
  },
  coexistenceButton: {
    backgroundColor: "#25D366",
    color: "#fff",
    fontWeight: "bold",
    padding: "10px 24px",
    borderRadius: 8,
    "&:hover": {
      backgroundColor: "#1EBE5A",
    },
  },
  embeddedSignupDivider: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2, 0),
    "& hr": {
      flex: 1,
      border: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& span": {
      padding: "0 12px",
      color: theme.palette.text.secondary,
      fontSize: "0.85rem",
    },
  },
  wabaSelector: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    ...panel("#e8f5e9", "76, 175, 80", "#4caf50"),
  },

  // O <Form> do Formik fica entre o Paper do Dialog e o DialogContent e quebra
  // o layout em coluna do scroll="paper": o popup inteiro rolava e os botões
  // Cancelar/Adicionar sumiam no fim. Com o form em coluna, só o conteúdo rola.
  dialogForm: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
  },

  // Caixas de aviso usadas nos blocos de status/validacao do formulario.
  // Espacamento continua vindo das props do Box; aqui so a cor.
  noticeSuccess: panel("#e8f5e9", "76, 175, 80", "#4caf50"),
  noticeStrongSuccess: panel("#c8e6c9", "76, 175, 80"),
  noticeWarning: panel("#fff3e0", "255, 152, 0", "#ff9800"),
  noticeError: panel("#ffebee", "244, 67, 54", "#f44336"),
  };
});

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, () => i18n.t("validation.tooShort"))
    .max(50, () => i18n.t("validation.tooLong"))
    .required(() => i18n.t("validation.required")),
});

// Canais com gerenciador de filas (menu de seleção) e mensagens automáticas
// de saudação/conclusão/avaliação/fora do expediente. Os demais canais
// (instagram, email e futuros) aceitam apenas 1 fila e não exibem esses campos.
const channelSupportsAutomation = (type) =>
  ["baileys", "cloudapi"].includes(type || "baileys");

// A API devolve `null` nas colunas opcionais (color, token, greetingMessage...).
// Passar null para um input controlado dispara o warning do React
// "`value` prop on `input` should not be null", então trocamos pelo vazio
// equivalente antes de alimentar o Formik.
const NULLABLE_TEXT_FIELDS = [
  "name",
  "color",
  "greetingMessage",
  "complationMessage",
  "outOfHoursMessage",
  "ratingMessage",
  "token",
  "expiresInactiveMessage",
  "timeToTransfer",
  "cloudApiPhoneNumberId",
  "cloudApiBusinessAccountId"
];

const NULLABLE_NUMBER_FIELDS = {
  expiresTicket: 0,
  timeUseBotQueues: 0,
  maxUseBotQueues: 3
};

const normalizeNullFields = (data) => {
  const normalized = { ...data };
  NULLABLE_TEXT_FIELDS.forEach((key) => {
    if (normalized[key] === null || normalized[key] === undefined) {
      normalized[key] = "";
    }
  });
  Object.entries(NULLABLE_NUMBER_FIELDS).forEach(([key, fallback]) => {
    if (normalized[key] === null || normalized[key] === undefined) {
      normalized[key] = fallback;
    }
  });
  return normalized;
};

// Mescla a config salva (emailConfig/smsConfig) sobre os defaults, trocando
// nulls persistidos pelo default — ex.: a conexão SMS grava `"rota": null`,
// que chegaria cru no TextField. Também garante que campos ausentes na config
// salva não deixem o input virar não-controlado.
const mergeChannelConfig = (defaults, loaded) => {
  const merged = { ...defaults };
  Object.entries(loaded || {}).forEach(([key, value]) => {
    merged[key] =
      value === null || value === undefined
        ? defaults[key] !== undefined
          ? defaults[key]
          : ""
        : value;
  });
  return merged;
};

// Protocolo de recebimento do formulário: só "imap" explícito é IMAP.
const getInboundProtocol = (emailConfig) =>
  emailConfig?.protocol === "imap" ? "imap" : "pop3";

// Protocolo de recebimento de uma conexão de email já salva. Conexão antiga não
// tem `protocol` gravado: era IMAP se tinha servidor IMAP preenchido. Sem esta
// regra o merge preencheria `protocol` com o default novo (pop3) e viraria
// conexões IMAP legadas.
const resolveInboundProtocol = (loaded) => {
  if (loaded?.protocol === "pop3") return "pop3";
  if (loaded?.protocol === "imap") return "imap";
  return String(loaded?.imapHost || "").trim() ? "imap" : "pop3";
};

const WhatsAppModal = ({
  open,
  onClose,
  whatsAppId,
  allowedConnectionType: allowedConnectionTypeProp = "both",
  initialType = null,
  allowQrConnection = true,
}) => {
  const classes = useStyles();
  // Plano sem WhatsApp via QR Code (`allowQrConnection` = false): o seletor fica
  // sempre no modo "both", sem o botão desse canal — Cloud API, Email,
  // Instagram e SMS continuam disponíveis.
  const allowedConnectionType = allowQrConnection ? allowedConnectionTypeProp : "both";
  // Tipo padrão de uma conexão nova. Ao editar, `initialType` vem da listagem
  // para que o modal já abra na aba do canal certo, sem esperar o fetch.
  const defaultConnectionType =
    !allowQrConnection || allowedConnectionType === "cloudapi" ? "cloudapi" : "baileys";
  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    onlineWhileViewing: false,
    token: "",
    provider: "beta",
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    // CAMPOS KANBAN
    kanbanAutoCreate: false,
    kanbanBoardId: null,
    kanbanColumnId: null,
    userCardsId: null,
    // CAMPOS CLOUD API
    type: initialType || defaultConnectionType,
    cloudApiPhoneNumberId: "",
    cloudApiBusinessAccountId: "",
    // CAMPOS EMAIL
    emailConfig: {
      // Protocolo de RECEBIMENTO sugerido para conexão nova. Conexão salva sem
      // `protocol` (legado) é tratada como IMAP em resolveInboundProtocol.
      protocol: "pop3",
      emailUser: "",
      emailPassword: "",
      fromName: "",
      pop3Host: "",
      pop3Port: 995,
      pop3Secure: true,
      pop3DeleteAfterFetch: false,
      imapHost: "",
      imapPort: 993,
      imapSecure: true,
      smtpHost: "",
      smtpPort: 587,
      smtpSecure: false,
      mailbox: "INBOX"
    },
    // CAMPOS SMS (gateways Witi.me / SMS Barato / SMSDev)
    smsConfig: {
      provider: "witime",
      apiKey: "",
      baseUrl: "",
      rota: "",
      tipoEnvio: "common",
      referencia: "",
      webhookToken: ""
    }
  };
  
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  
  // ESTADOS PARA KANBAN
  const [kanbanBoards, setKanbanBoards] = useState([]);
  const [kanbanColumns, setKanbanColumns] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [users, setUsers] = useState([]);
  
  // ESTADOS PARA CLOUD API
  const [cloudApiValidation, setCloudApiValidation] = useState(null); // null | { valid, ... }
  const [validating, setValidating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // ESTADOS PARA EMBEDDED SIGNUP
  const [embeddedSignupLoading, setEmbeddedSignupLoading] = useState(false);
  // Qual botão abriu a tela da Meta: número novo na API ou app do celular (coexistência)
  const [embeddedSignupCoexistence, setEmbeddedSignupCoexistence] = useState(false);
  const [embeddedSignupResult, setEmbeddedSignupResult] = useState(null);
  const [facebookAppId, setFacebookAppId] = useState("");
  const [configId, setConfigId] = useState("");
  const [connectionMode, setConnectionMode] = useState("embedded"); // "embedded" | "manual"
  // Otimista: só desabilita o botão quando o backend responde que não está
  // configurado. Se a consulta falhar, deixa clicar — o erro real vem do backend.
  const [instagramSignupEnabled, setInstagramSignupEnabled] = useState(true);
  const [testingEmailConnection, setTestingEmailConnection] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [instagramLoginLoading, setInstagramLoginLoading] = useState(false);
  // 2026-03-26 Copilot: add toggle to show/hide email password.
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  // Segredos ocultos por padrão (padrão password + olho do projeto).
  const [showSmsApiKey, setShowSmsApiKey] = useState(false);
  const [showConnectionToken, setShowConnectionToken] = useState(false);
  // "Remover token": o campo vazio passou a significar "manter o atual".
  const [removeToken, setRemoveToken] = useState(false);
  const [showWebhookUrl, setShowWebhookUrl] = useState(false);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  
  // Ao abrir o modal já posiciona o formulário no tipo da conexão selecionada.
  // O fetch abaixo é assíncrono (e para email/SMS são duas requisições), então
  // sem isso o modal abriria sempre em "Baileys" e só depois trocaria de aba.
  useEffect(() => {
    if (!open) return;
    setWhatsApp(prev => ({
      ...prev,
      type: initialType || defaultConnectionType,
    }));
  }, [open, initialType, defaultConnectionType]);

  useEffect(() => {
    const fetchSession = async () => {
      setRemoveToken(false);
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        // Email e SMS guardam a configuração dentro de `session`, que só vem na
        // consulta completa (o backend exclui `session` por padrão).
        if (data.type === "email" || data.type === "sms") {
          const { data: fullData } = await api.get(`whatsapp/${whatsAppId}`);
          let sessionData = fullData?.session || {};
          if (typeof sessionData === "string") {
            try {
              sessionData = JSON.parse(sessionData);
            } catch (_err) {
              sessionData = {};
            }
          }

          if (data.type === "email") {
            const loadedEmailConfig = sessionData?.emailConfig;
            const emailConfig = mergeChannelConfig(
              initialState.emailConfig,
              loadedEmailConfig
            );
            emailConfig.protocol = resolveInboundProtocol(loadedEmailConfig);
            setWhatsApp({
              ...normalizeNullFields(fullData),
              emailConfig
            });
          } else {
            setWhatsApp({
              ...normalizeNullFields(fullData),
              smsConfig: mergeChannelConfig(
                initialState.smsConfig,
                sessionData?.smsConfig
              )
            });
          }
        } else {
          // Mantém o default do smsConfig para que os campos continuem
          // controlados caso o usuário troque o tipo da conexão para SMS.
          setWhatsApp({
            ...normalizeNullFields(data),
            smsConfig: initialState.smsConfig
          });
        }

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
        setSelectedQueueId(data.transferQueueId);
        // Sem isto o submit sempre enviaria promptId: null e apagaria o prompt
        // da conexão só por abrir e salvar o modal.
        setSelectedPrompt(data.promptId || null);

        // Carregar board selecionado
        if (data.kanbanBoardId) {
          setSelectedBoard(data.kanbanBoardId);
          loadBoardColumns(data.kanbanBoardId);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  // Carregar boards ao abrir modal
  useEffect(() => {
    const loadKanbanBoards = async () => {
      try {
        // summary: só nome/id; as colunas vêm de /kanban/boards/:id
        const { data } = await api.get("/kanban/boards", { params: { summary: true } });
        setKanbanBoards(data.boards || []);
      } catch (err) {
        console.error("Erro ao carregar boards:", err);
      }
    };
    
    if (open) {
      loadKanbanBoards();
    }
  }, [open]);

  // Credenciais do app da Meta (configuradas uma única vez no .env do servidor).
  // O endpoint devolve apenas dados públicos: App ID, Config ID e o que está habilitado.
  useEffect(() => {
    const loadMetaAppConfig = async () => {
      try {
        const { data } = await api.get("/meta/app-config");
        setFacebookAppId(data?.whatsapp?.appId || "");
        setConfigId(data?.whatsapp?.configId || "");
        setInstagramSignupEnabled(Boolean(data?.instagram?.enabled));
      } catch (err) {
        console.error("Erro ao carregar configurações do app Meta:", err);
      }
    };

    if (open) {
      loadMetaAppConfig();
    }
  }, [open]);

  // Carregar colunas quando board for selecionado
  const loadBoardColumns = async (boardId) => {
    if (!boardId) {
      setKanbanColumns([]);
      return;
    }

    try {
      const { data } = await api.get(`/kanban/boards/${boardId}`);
      setKanbanColumns(data.columns || []);
    } catch (err) {
      console.error("Erro ao carregar colunas:", err);
      toast.error(i18n.t("whatsappModal.kanban.loadColumnsError"));
    }
  };

  const handleBoardChange = (e, setFieldValue) => {
    const boardId = e.target.value;
    setSelectedBoard(boardId);
    loadBoardColumns(boardId);
    
    // Resetar coluna selecionada quando mudar de board
    setFieldValue("kanbanBoardId", boardId);
    setFieldValue("kanbanColumnId", null);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Carregar usuários para o seletor
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users");
        setUsers(data.users || []);
      } catch (err) {
        console.error("Erro ao carregar usuários:", err);
      }
    };
    
    if (open) {
      loadUsers();
    }
  }, [open]);

  const handleConnectInstagram = async () => {
    try {
      setInstagramLoginLoading(true);
      await startInstagramLogin();
      // startInstagramLogin redireciona a página; o setState abaixo raramente roda.
    } catch (err) {
      setInstagramLoginLoading(false);
      toastError(err);
    }
  };

  // O backend nunca devolve o segredo real (só `hasPassword`/`hasApiKey` +
  // `apiKeyHint`). No submit, campo vazio ou placeholder mascarado é removido
  // do payload para o backend manter o valor já salvo; os campos derivados do
  // serializer também não devem voltar.
  const buildSecretPayload = (config, secretField) => {
    if (!config) return config;
    const next = { ...config };
    delete next.hasApiKey;
    delete next.apiKeyHint;
    delete next.hasPassword;
    const secret = String(next[secretField] || "").trim();
    if (!secret || secret.startsWith("*")) {
      delete next[secretField];
    }
    return next;
  };

  const handleSaveWhatsApp = async (values) => {
    if (values.type === "email") {
      const emailConfig = values.emailConfig || {};
      const hasStoredPassword = Boolean(whatsAppId && emailConfig.hasPassword);
      const inboundProtocol = getInboundProtocol(emailConfig);
      const inboundHost = inboundProtocol === "imap" ? emailConfig.imapHost : emailConfig.pop3Host;
      if (!emailConfig.emailUser || (!emailConfig.emailPassword && !hasStoredPassword) || !inboundHost || !emailConfig.smtpHost) {
        toast.error(i18n.t("whatsappModal.email.toasts.missingFields", { protocol: inboundProtocol.toUpperCase() }));
        return;
      }
    }

    if (values.type === "sms") {
      const hasStoredKey = Boolean(whatsAppId && values.smsConfig?.hasApiKey);
      if (!values.smsConfig?.apiKey && !hasStoredKey) {
        toast.error(i18n.t("whatsappModal.sms.apiKeyMissing"));
        return;
      }
    }

    const supportsAutomation = channelSupportsAutomation(values.type);

    const whatsappData = {
      ...values,
      queueIds: supportsAutomation
        ? selectedQueueIds
        : (selectedQueueIds || []).slice(0, 1),
      transferQueueId: selectedQueueId,
      promptId: selectedPrompt ? selectedPrompt : null,
      // Campos do Kanban
      kanbanAutoCreate: values.kanbanAutoCreate,
      kanbanBoardId: values.kanbanBoardId || null,
      kanbanColumnId: values.kanbanColumnId || null,
      userCardsId: values.userCardsId || null,
      // Campos Cloud API
      type: values.type || (whatsAppId ? "baileys" : defaultConnectionType),
      cloudApiPhoneNumberId: values.cloudApiPhoneNumberId || null,
      cloudApiBusinessAccountId: values.cloudApiBusinessAccountId || null,
      // Campos Email
      emailConfig: values.type === "email" ? buildSecretPayload(values.emailConfig, "emailPassword") : undefined,
      // Campos SMS (Witi.me)
      smsConfig: values.type === "sms" ? buildSecretPayload(values.smsConfig, "apiKey") : undefined
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];
    delete whatsappData["cloudApiAccessToken"];
    // Campos derivados do serializer não voltam para o backend.
    ["hasToken", "tokenHint", "hasSession", "smsProvider"].forEach(key => {
      delete whatsappData[key];
    });
    // O backend nunca devolve o token salvo: vazio/máscara mantém o atual (a
    // chave nem vai), valor novo troca e "Remover token" manda null.
    const typedToken = String(values.token || "").trim();
    if (whatsAppId) {
      if (removeToken) {
        whatsappData.token = null;
      } else if (!typedToken || typedToken.startsWith("*")) {
        delete whatsappData.token;
      } else {
        whatsappData.token = typedToken;
      }
    } else {
      whatsappData.token = typedToken || null;
    }
    // Cor vazia não é enviada: na criação o backend sorteia uma cor e na
    // edição evita apagar a cor existente
    if (!whatsappData.color) delete whatsappData.color;

    // Canais sem automação não usam mensagens de saudação/conclusão/avaliação/expediente.
    if (!supportsAutomation) {
      whatsappData.greetingMessage = "";
      whatsappData.complationMessage = "";
      whatsappData.outOfHoursMessage = "";
      whatsappData.ratingMessage = "";
    }

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t(`whatsappModal.successByType.${whatsappData.type}`, {
        defaultValue: i18n.t("whatsappModal.success"),
      }));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  // Validar credenciais Cloud API
  const handleValidateCloudApi = async (values) => {
    setValidating(true);
    setCloudApiValidation(null);
    try {
      const { data } = await api.post("/cloudapi/validate", {
        cloudApiPhoneNumberId: values.cloudApiPhoneNumberId,
        cloudApiBusinessAccountId: values.cloudApiBusinessAccountId,
      });
      setCloudApiValidation(data);
      if (data.valid) {
        toast.success(i18n.t("whatsappModal.cloudApi.toasts.valid", { phone: data.phoneNumber, name: data.verifiedName }));
      } else {
        toast.error(i18n.t("whatsappModal.cloudApi.toasts.invalid", { error: data.error }));
      }
    } catch (err) {
      toast.error(i18n.t("whatsappModal.cloudApi.toasts.validateError"));
      setCloudApiValidation({ valid: false, error: i18n.t("whatsappModal.cloudApi.toasts.communicationError") });
    }
    setValidating(false);
  };

  // Handler do Embedded Signup
  // Preenche o nome da conexão a partir do número conectado, sem sobrescrever
  // um nome que o usuário já tenha digitado.
  const fillConnectionName = (values, setFieldValue, suggestion) => {
    if (!suggestion) return;
    if (String(values?.name || "").trim()) return;
    setFieldValue("name", suggestion);
  };

  // `coexistence`: botão "WhatsApp Business do celular" — a tela da Meta oferece
  // conectar o app existente (o número continua no celular).
  const handleEmbeddedSignup = async (values, setFieldValue, { coexistence = false } = {}) => {
    if (!facebookAppId || !configId) {
      toast.error(
        i18n.t("whatsappModal.cloudApi.toasts.signupUnavailable")
      );
      return;
    }

    setEmbeddedSignupLoading(true);
    setEmbeddedSignupCoexistence(coexistence);
    setEmbeddedSignupResult(null);

    // Listener para capturar info direta do Facebook (phone_number_id, waba_id)
    let signupInfo = null;
    const infoHandler = (event) => {
      signupInfo = event.detail;
    };
    window.addEventListener("wa-embedded-signup-info", infoHandler);

    launchWhatsAppSignup({
      appId: facebookAppId,
      configId: configId,
      coexistence,
      onSuccess: async (code) => {
        window.removeEventListener("wa-embedded-signup-info", infoHandler);
        
        try {
          toast.info(i18n.t("whatsappModal.cloudApi.toasts.processing"));

          // Enviar código para o backend trocar por token e buscar WABAs. O
          // session-info-event já trouxe a WABA e o número desta sessão: manda
          // junto para o backend não depender só da inspeção do token.
          const { data } = await api.post(
            "/cloudapi/embedded-signup",
            {
              code,
              wabaId: signupInfo?.waba_id,
              phoneNumberId: signupInfo?.phone_number_id,
              // Só a Meta sabe se a pessoa conectou o app do celular ou um número
              // novo: com o app, o backend não pode registrar o número de novo.
              coexistence:
                signupInfo?.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
            },
            // O backend encadeia várias chamadas à Graph API (token, WABA,
            // números, webhooks, registro). Os 30s padrão do cliente cancelavam
            // o signup no meio, com a conta já criada do lado da Meta.
            { timeout: 120000 }
          );

          if (!data.success) {
            toast.error(data.error || i18n.t("whatsappModal.cloudApi.toasts.signupError"));
            setEmbeddedSignupLoading(false);
            return;
          }

          setEmbeddedSignupResult(data);

          // Número em mais de uma conta de mensagens da Meta: a sessão informa
          // uma conta, mas o número pode enviar (e cobrar) por outra. Não
          // preenche com a conta da sessão; usa a sugerida pelo backend, se
          // houver, e deixa o seletor com o aviso para a pessoa confirmar.
          if (data.conflict) {
            const suggestedWaba = (data.wabas || []).find((w) => w.id === data.conflict.suggestedWabaId);
            const phoneId = data.conflict.phoneNumberId || signupInfo?.phone_number_id;
            const suggestedPhone = suggestedWaba?.phone_numbers?.find((p) => p.id === phoneId);
            if (suggestedWaba && suggestedPhone) {
              setFieldValue("cloudApiPhoneNumberId", suggestedPhone.id);
              setFieldValue("cloudApiBusinessAccountId", suggestedWaba.id);
              fillConnectionName(
                values,
                setFieldValue,
                suggestedPhone.verified_name || suggestedPhone.display_phone_number
              );
            } else {
              setFieldValue("cloudApiPhoneNumberId", "");
              setFieldValue("cloudApiBusinessAccountId", "");
            }
            toast.warn(
              i18n.t("whatsappModal.cloudApi.toasts.wabaConflict", { count: data.conflict.wabaIds.length }),
              { autoClose: 12000 }
            );
          }
          // Se o Facebook já enviou phone_number_id e waba_id diretamente
          else if (signupInfo && signupInfo.phone_number_id && signupInfo.waba_id) {
            setFieldValue("cloudApiPhoneNumberId", signupInfo.phone_number_id);
            setFieldValue("cloudApiBusinessAccountId", signupInfo.waba_id);
            // O nome só vem quando o backend também devolveu a WABA correspondente
            const matchedPhone = data.wabas
              ?.flatMap((w) => w.phone_numbers || [])
              ?.find((p) => p.id === signupInfo.phone_number_id);
            fillConnectionName(
              values,
              setFieldValue,
              matchedPhone?.verified_name || matchedPhone?.display_phone_number
            );
            toast.success(i18n.t("whatsappModal.cloudApi.toasts.connectedReview"));
          }
          // Se só temos 1 WABA com 1 número, preencher automaticamente
          else if (data.wabas && data.wabas.length === 1 && data.wabas[0].phone_numbers.length === 1) {
            const waba = data.wabas[0];
            const phone = waba.phone_numbers[0];
            setFieldValue("cloudApiPhoneNumberId", phone.id);
            setFieldValue("cloudApiBusinessAccountId", waba.id);
            fillConnectionName(values, setFieldValue, phone.verified_name || phone.display_phone_number);
            toast.success(i18n.t("whatsappModal.cloudApi.toasts.connectedNumber", { phone: phone.display_phone_number, name: phone.verified_name }));
          }
          // Se existem múltiplos WABAs/números, mostrar seletor
          else if (data.wabas && data.wabas.length > 0) {
            toast.success(i18n.t("whatsappModal.cloudApi.toasts.authorizedSelect"));
          } else {
            toast.warn(i18n.t("whatsappModal.cloudApi.toasts.authorizedNoNumber"));
          }
        } catch (err) {
          // Timeout tem tratamento próprio: a conta já foi criada na Meta, então
          // o caminho é reabrir a tela e não repetir o fluxo do zero.
          const timedOut = err?.code === "ECONNABORTED";
          toast.error(
            timedOut
              ? i18n.t("whatsappModal.cloudApi.toasts.timeout")
              : err?.response?.data?.error || i18n.t("whatsappModal.cloudApi.toasts.processError")
          );
          console.error("Embedded Signup error:", err);
        }

        setEmbeddedSignupLoading(false);
      },
      onError: (errorMsg) => {
        window.removeEventListener("wa-embedded-signup-info", infoHandler);
        toast.error(errorMsg || i18n.t("whatsappModal.cloudApi.toasts.cancelled"));
        setEmbeddedSignupLoading(false);
      },
    });
  };

  const handleTestEmailConnection = async (values) => {
    const emailConfig = values?.emailConfig || {};
    setEmailTestResult(null);

    // Em conexão já salva a senha não volta do backend (só `hasPassword`);
    // o teste envia `whatsAppId` e o backend completa a senha armazenada.
    const hasUsablePassword =
      Boolean(emailConfig.emailPassword) || Boolean(whatsAppId && emailConfig.hasPassword);

    const inboundProtocol = getInboundProtocol(emailConfig);
    const inboundLabel = inboundProtocol.toUpperCase();
    const inboundHost = inboundProtocol === "imap" ? emailConfig.imapHost : emailConfig.pop3Host;

    if (!emailConfig.emailUser || !hasUsablePassword || !inboundHost || !emailConfig.smtpHost) {
      const missingInbound = {
        protocol: inboundProtocol,
        success: false,
        message: i18n.t("whatsappModal.email.toasts.missingServer", { protocol: inboundLabel })
      };
      const missingResult = {
        success: false,
        inbound: missingInbound,
        imap: missingInbound,
        smtp: {
          success: false,
          message: i18n.t("whatsappModal.email.toasts.missingServer", { protocol: "SMTP" })
        }
      };
      setEmailTestResult(missingResult);
      toast.error(i18n.t("whatsappModal.email.toasts.missingFieldsTest", { protocol: inboundLabel }));
      return;
    }

    setTestingEmailConnection(true);
    try {
      const { data } = await api.post("/whatsapp/email/test-connection", {
        emailConfig: buildSecretPayload(emailConfig, "emailPassword"),
        whatsappId: whatsAppId || undefined
      });

      setEmailTestResult(data);

      // Backend novo devolve `inbound` (POP3 ou IMAP); `imap` é o alias antigo.
      const inbound = data?.inbound || data?.imap;
      const resultLabel = String(inbound?.protocol || inboundProtocol).toUpperCase();
      const failedShort = i18n.t("whatsappModal.email.toasts.failedShort");

      if (data?.success) {
        toast.success(i18n.t("whatsappModal.email.toasts.validated", { protocol: resultLabel }));
      } else if (inbound?.success || data?.smtp?.success) {
        toast.warn(i18n.t("whatsappModal.email.toasts.partialDetail", {
          protocol: resultLabel,
          inbound: inbound?.message || failedShort,
          smtp: data?.smtp?.message || failedShort,
        }));
      } else {
        toast.error(`${resultLabel}: ${inbound?.message || failedShort} | SMTP: ${data?.smtp?.message || failedShort}`);
      }
    } catch (err) {
      const responseData = err?.response?.data;
      const responseInbound = responseData?.inbound || responseData?.imap;

      if (responseData && (responseInbound || responseData.smtp)) {
        setEmailTestResult(responseData);
        if (responseInbound?.success || responseData?.smtp?.success) {
          toast.warn(i18n.t("whatsappModal.email.toasts.partial"));
        } else {
          toast.error(i18n.t("whatsappModal.email.toasts.failed"));
        }
      } else {
        const failedInbound = {
          protocol: inboundProtocol,
          success: false,
          message: i18n.t("whatsappModal.email.toasts.testFailed", { protocol: inboundLabel })
        };
        setEmailTestResult({
          success: false,
          inbound: failedInbound,
          imap: failedInbound,
          smtp: { success: false, message: i18n.t("whatsappModal.email.toasts.testFailed", { protocol: "SMTP" }) }
        });
        toastError(err);
      }
    } finally {
      setTestingEmailConnection(false);
    }
  };

  // Selecionar um número do resultado do Embedded Signup
  const handleSelectSignupPhone = (waba, phone, values, setFieldValue) => {
    setFieldValue("cloudApiPhoneNumberId", phone.id);
    setFieldValue("cloudApiBusinessAccountId", waba.id);
    fillConnectionName(values, setFieldValue, phone.verified_name || phone.display_phone_number);
    toast.success(i18n.t("whatsappModal.cloudApi.toasts.numberSelected", { phone: phone.display_phone_number, name: phone.verified_name }));
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    const promptId = e.target.value || null;
    setSelectedPrompt(promptId);
    // Prompt e filas são mutuamente exclusivos, mas escolher "Nenhum" só remove
    // o prompt — não pode levar as filas junto.
    if (promptId) {
      setSelectedQueueIds([]);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setRemoveToken(false);
    setSelectedQueueId(null);
    setSelectedQueueIds([]);
    setSelectedPrompt(null);
    setSelectedBoard(null);
    setKanbanColumns([]);
    setCloudApiValidation(null);
    setActiveTab(0);
    setEmbeddedSignupResult(null);
    setConnectionMode("embedded");
    setEmailTestResult(null);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form className={classes.dialogForm}>
              <DialogContent dividers>
                {/* ===== SELETOR DE TIPO DE API ===== */}
                {allowedConnectionType === "both" && (
                <Box className={classes.apiTypeSelector}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{i18n.t("whatsappModal.connectionType.title")}</strong>
                  </Typography>
                  <Grid container spacing={2}>
                    {allowQrConnection && (
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant={values.type === "baileys" ? "contained" : "outlined"}
                        color={values.type === "baileys" ? "primary" : "default"}
                        onClick={() => setFieldValue("type", "baileys")}
                      >
                        {i18n.t("whatsappModal.connectionType.baileys")}
                      </Button>
                    </Grid>
                    )}
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant={values.type === "cloudapi" ? "contained" : "outlined"}
                        color={values.type === "cloudapi" ? "primary" : "default"}
                        onClick={() => setFieldValue("type", "cloudapi")}
                        style={values.type === "cloudapi" ? { backgroundColor: "#25D366", color: "#fff" } : {}}
                      >
                        {i18n.t("whatsappModal.connectionType.cloudapi")}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant={values.type === "email" ? "contained" : "outlined"}
                        color={values.type === "email" ? "primary" : "default"}
                        onClick={() => setFieldValue("type", "email")}
                      >
                        {i18n.t("whatsappModal.connectionType.email")}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant={values.type === "instagram" ? "contained" : "outlined"}
                        color={values.type === "instagram" ? "primary" : "default"}
                        onClick={() => setFieldValue("type", "instagram")}
                        style={values.type === "instagram" ? { background: "linear-gradient(45deg,#833AB4,#E1306C,#F77737)", color: "#fff" } : {}}
                      >
                        Instagram
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant={values.type === "sms" ? "contained" : "outlined"}
                        color={values.type === "sms" ? "primary" : "default"}
                        onClick={() => setFieldValue("type", "sms")}
                        style={values.type === "sms" ? { backgroundColor: "#F5A623", color: "#fff" } : {}}
                      >
                        SMS
                      </Button>
                    </Grid>
                  </Grid>
                  {values.type === "cloudapi" && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                      {i18n.t("whatsappModal.connectionType.desc.cloudapi")}
                    </Typography>
                  )}
                  {values.type === "email" && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                      {i18n.t("whatsappModal.connectionType.desc.email")}
                    </Typography>
                  )}
                  {values.type === "instagram" && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                      {i18n.t("whatsappModal.connectionType.desc.instagram")}
                    </Typography>
                  )}
                  {values.type === "sms" && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                      {i18n.t("whatsappModal.connectionType.desc.sms")}
                    </Typography>
                  )}
                  {allowQrConnection && values.type === "baileys" && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
                      {i18n.t("whatsappModal.connectionType.desc.baileys")}
                    </Typography>
                  )}
                </Box>
                )}
                {allowedConnectionType !== "both" && (
                <Box className={classes.apiTypeSelector}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{i18n.t("whatsappModal.connectionType.fixed", {
                      type: allowedConnectionType === "cloudapi"
                        ? i18n.t("whatsappModal.connectionType.cloudapi")
                        : i18n.t("whatsappModal.connectionType.baileys"),
                    })}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {allowedConnectionType === "cloudapi"
                      ? i18n.t("whatsappModal.connectionType.desc.cloudapi")
                      : i18n.t("whatsappModal.connectionType.desc.baileys")}
                  </Typography>
                </Box>
                )}

                {/* ===== CAMPOS GERAIS ===== */}
                <div className={classes.multFieldLine}>
                  <Grid spacing={2} container>
                    <Grid item>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.name")}
                        autoFocus
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.color")}
                        name="color"
                        id="color"
                        style={{ maxWidth: 140 }}
                        onClick={() => setColorPickerModalOpen(true)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <div
                                style={{ backgroundColor: values.color || "#7c7c7c" }}
                                className={classes.colorAdorment}
                              ></div>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => setColorPickerModalOpen(true)}
                            >
                              <Colorize />
                            </IconButton>
                          ),
                        }}
                        variant="outlined"
                        margin="dense"
                      />
                      <ColorPicker
                        open={colorPickerModalOpen}
                        handleClose={() => setColorPickerModalOpen(false)}
                        onChange={(color) => {
                          values.color = color;
                          setWhatsApp(() => {
                            return { ...values, color };
                          });
                        }}
                      />
                    </Grid>
                    <Grid style={{ paddingTop: 15 }} item>
                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="isDefault"
                            checked={values.isDefault}
                          />
                        }
                        label={i18n.t("whatsappModal.form.default")}
                      />
                    </Grid>
                  </Grid>
                </div>

                {/* Número online para receber o "digitando": silencia o celular */}
                {values.type === "baileys" && (
                  <Box mt={1}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          color="primary"
                          name="onlineWhileViewing"
                          checked={!!values.onlineWhileViewing}
                        />
                      }
                      label={i18n.t("whatsappModal.form.onlineWhileViewing")}
                    />
                    <Typography variant="caption" color="error" style={{ display: "block" }}>
                      {i18n.t("whatsappModal.form.onlineWhileViewingHelp")}
                    </Typography>
                  </Box>
                )}

                {/* ===== CONFIGURAÇÃO DA CLOUD API (apenas se tipo = cloudapi) ===== */}
                {values.type === "cloudapi" && (
                  <Box className={classes.cloudApiSection}>
                    <Typography variant="h6" gutterBottom>
                      {i18n.t("whatsappModal.cloudApi.title")}
                    </Typography>

                    {/* Sem credenciais do app no servidor, só resta o caminho manual */}
                    {!whatsAppId && (!facebookAppId || !configId) && (
                      <Typography variant="body2" color="error" gutterBottom>
                        {i18n.t("whatsappModal.cloudApi.appNotConfigured")}
                      </Typography>
                    )}

                    {/* ===== OPÇÃO 1: EMBEDDED SIGNUP (1 clique) ===== */}
                    {!whatsAppId && facebookAppId && configId && (
                      <>
                        {/* O convite ao login sai de cena assim que a autorização
                            traz contas: daí em diante o que importa é escolher o
                            número, não repetir o fluxo do Facebook. */}
                        {!embeddedSignupResult?.wabas?.length && (
                          <>
                          {/* Coexistência: o número continua no app do celular.
                              Vem primeiro por ser o caso mais comum; os limites
                              ficam à vista ANTES de abrir a tela da Meta. */}
                          <Box className={classes.coexistenceSection}>
                            <Typography variant="subtitle1" gutterBottom>
                              <strong>{i18n.t("whatsappModal.cloudApi.coexistence.title")}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {i18n.t("whatsappModal.cloudApi.coexistence.description")}
                            </Typography>
                            <Typography variant="body2" component="ul" color="textSecondary">
                              {["groups", "calls", "inactivity", "pricing", "history", "appVersion"].map((key) => (
                                <li key={key}>{i18n.t(`whatsappModal.cloudApi.coexistence.limits.${key}`)}</li>
                              ))}
                            </Typography>
                            <Box textAlign="center">
                              <Button
                                className={classes.coexistenceButton}
                                onClick={() => handleEmbeddedSignup(values, setFieldValue, { coexistence: true })}
                                disabled={embeddedSignupLoading}
                              >
                                {embeddedSignupLoading && embeddedSignupCoexistence && (
                                  <CircularProgress size={20} style={{ color: "#fff", marginRight: 8 }} />
                                )}
                                {embeddedSignupLoading && embeddedSignupCoexistence
                                  ? i18n.t("whatsappModal.cloudApi.connecting")
                                  : i18n.t("whatsappModal.cloudApi.coexistence.connect")}
                              </Button>
                            </Box>
                          </Box>

                          <Box className={classes.embeddedSignupSection}>
                            <Typography variant="subtitle1" gutterBottom>
                              <strong>{i18n.t("whatsappModal.cloudApi.quickTitle")}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                              {i18n.t("whatsappModal.cloudApi.quickDescription")}
                            </Typography>
                            <Button
                              className={classes.embeddedSignupButton}
                              onClick={() => handleEmbeddedSignup(values, setFieldValue)}
                              disabled={embeddedSignupLoading}
                            >
                              {embeddedSignupLoading && !embeddedSignupCoexistence && (
                                <CircularProgress size={20} style={{ color: "#fff", marginRight: 8 }} />
                              )}
                              {embeddedSignupLoading && !embeddedSignupCoexistence ? i18n.t("whatsappModal.cloudApi.connecting") : i18n.t("whatsappModal.cloudApi.connect")}
                            </Button>
                          </Box>
                          </>
                        )}

                        {/* Número em mais de uma conta de mensagens da Meta */}
                        {embeddedSignupResult?.conflict && (
                          <Box className={classes.wabaSelector} style={{ borderLeft: "4px solid #f0ad4e" }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>
                                {i18n.t("whatsappModal.cloudApi.wabaConflictTitle", {
                                  count: embeddedSignupResult.conflict.wabaIds.length,
                                })}
                              </strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {i18n.t("whatsappModal.cloudApi.wabaConflictHelp")}
                            </Typography>
                          </Box>
                        )}

                        {/* Seletor de WABA/Número quando há múltiplos resultados */}
                        {embeddedSignupResult && embeddedSignupResult.wabas && embeddedSignupResult.wabas.length > 0 && (
                          embeddedSignupResult.wabas.some(w => w.phone_numbers.length > 1) || embeddedSignupResult.wabas.length > 1
                        ) && (
                          <Box className={classes.wabaSelector}>
                            <Typography variant="subtitle2" gutterBottom>
                              <strong>{i18n.t("whatsappModal.cloudApi.selectNumber")}</strong>
                            </Typography>
                            {embeddedSignupResult.wabas.map((waba) => (
                              <Box key={waba.id} mb={1}>
                                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                                  {waba.name} (WABA: {waba.id})
                                  {waba.suggested && ` — ${i18n.t("whatsappModal.cloudApi.wabaSuggested")}`}
                                  {(waba.hasConnection || waba.approvedTemplates > 0) && (
                                    <span style={{ fontWeight: 400, color: "#666" }}>
                                      {" "}
                                      ({[
                                        waba.hasConnection ? i18n.t("whatsappModal.cloudApi.wabaHasConnection") : null,
                                        waba.approvedTemplates > 0
                                          ? i18n.t("whatsappModal.cloudApi.wabaApprovedTemplates", { count: waba.approvedTemplates })
                                          : null,
                                      ].filter(Boolean).join(", ")})
                                    </span>
                                  )}
                                </Typography>
                                {waba.phone_numbers.map((phone) => (
                                  <Button
                                    key={phone.id}
                                    variant={values.cloudApiPhoneNumberId === phone.id ? "contained" : "outlined"}
                                    color="primary"
                                    size="small"
                                    style={{ margin: "2px 4px" }}
                                    onClick={() => handleSelectSignupPhone(waba, phone, values, setFieldValue)}
                                  >
                                    {phone.display_phone_number} — {phone.verified_name}
                                  </Button>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        )}

                        <div className={classes.embeddedSignupDivider}>
                          <hr />
                          <span>{i18n.t("whatsappModal.cloudApi.orManual")}</span>
                          <hr />
                        </div>

                        <Button
                          variant="text"
                          color="default"
                          size="small"
                          onClick={() => setConnectionMode(connectionMode === "manual" ? "embedded" : "manual")}
                          style={{ marginBottom: 8 }}
                        >
                          {connectionMode === "manual"
                            ? `▲ ${i18n.t("whatsappModal.cloudApi.hideManual")}`
                            : `▼ ${i18n.t("whatsappModal.cloudApi.showManual")}`}
                        </Button>
                      </>
                    )}

                    {/* ===== OPÇÃO 2: CONFIGURAÇÃO MANUAL (campos WABA ID e Phone Number ID) ===== */}
                    {(connectionMode === "manual" || !facebookAppId || !configId || whatsAppId) && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Phone Number ID *"
                          name="cloudApiPhoneNumberId"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("whatsappModal.cloudApi.phoneNumberIdHelp")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Business Account ID (WABA) *"
                          name="cloudApiBusinessAccountId"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("whatsappModal.cloudApi.wabaIdHelp")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} style={{ display: "flex", alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleValidateCloudApi(values)}
                          disabled={validating || !values.cloudApiPhoneNumberId || !values.cloudApiBusinessAccountId}
                          fullWidth
                        >
                          {validating ? (
                            <CircularProgress size={20} style={{ marginRight: 8 }} />
                          ) : null}
                          {i18n.t("whatsappModal.cloudApi.validate")}
                        </Button>
                        {cloudApiValidation && (
                          <Chip
                            className={classes.validationChip}
                            icon={cloudApiValidation.valid ? <CheckCircle /> : <ErrorIcon />}
                            label={cloudApiValidation.valid ? i18n.t("whatsappModal.cloudApi.valid") : i18n.t("whatsappModal.cloudApi.invalid")}
                            color={cloudApiValidation.valid ? "primary" : "secondary"}
                            size="small"
                          />
                        )}
                      </Grid>
                    </Grid>
                    )}

                    {cloudApiValidation?.valid && (
                      <Box mt={2} p={1} borderRadius={4} className={classes.noticeStrongSuccess}>
                        <Typography variant="body2">
                          <strong>{i18n.t("whatsappModal.cloudApi.result.number")}</strong> {cloudApiValidation.phoneNumber}<br />
                          <strong>{i18n.t("whatsappModal.cloudApi.result.verifiedName")}</strong> {cloudApiValidation.verifiedName}<br />
                          <strong>{i18n.t("whatsappModal.cloudApi.result.quality")}</strong> {cloudApiValidation.qualityRating}<br />
                          <strong>{i18n.t("whatsappModal.cloudApi.result.account")}</strong> {cloudApiValidation.accountName}
                        </Typography>
                      </Box>
                    )}

                    {/* Status dos campos preenchidos (via Embedded Signup ou manual) */}
                    {values.cloudApiPhoneNumberId && values.cloudApiBusinessAccountId && (
                      <Box mt={2} p={1} borderRadius={4} className={classes.noticeSuccess}>
                        <Typography variant="body2">
                          <strong>{i18n.t("whatsappModal.cloudApi.credentialsSet")}</strong><br />
                          Phone Number ID: <code>{values.cloudApiPhoneNumberId}</code><br />
                          WABA ID: <code>{values.cloudApiBusinessAccountId}</code>
                        </Typography>
                      </Box>
                    )}

                    {/* App Secret, Verify Token, App ID e Config ID são do app da
                        Meta do provedor e vivem no .env do servidor (uma única vez,
                        para todas as empresas). O aviso só faz sentido enquanto o
                        servidor não tem essas credenciais — aí a aba Cloud API em
                        Configurações ainda funciona como fallback. */}
                    {(!facebookAppId || !configId) && (
                      <Box mt={2} p={1} borderRadius={4} className={classes.noticeWarning}>
                        <Typography variant="body2">
                          <strong>{i18n.t("whatsappModal.cloudApi.appCredentialsLabel")}</strong>{" "}
                          {i18n.t("whatsappModal.cloudApi.appCredentialsText")}
                        </Typography>
                      </Box>
                    )}

                    {/* Coexistência: estado da importação única de contatos e
                        histórico do celular + lembrete dos 14 dias */}
                    {whatsAppId && whatsApp?.type === "cloudapi" && whatsApp?.cloudApiCoexistence && (
                      <CoexistenceStatus whatsApp={whatsApp} className={classes.noticeSuccess} />
                    )}

                    {/* Ligações pelo WhatsApp: só com a conexão Cloud API já salva.
                        Número que continua no app do celular não liga pela API. */}
                    {whatsAppId && whatsApp?.type === "cloudapi" && !whatsApp?.cloudApiCoexistence && (
                      <CallingSettingsSection whatsAppId={whatsAppId} />
                    )}
                  </Box>
                )}

                {/* ===== CONEXÃO DO INSTAGRAM (apenas se tipo = instagram) ===== */}
                {values.type === "instagram" && (
                  <Box className={classes.cloudApiSection}>
                    <Typography variant="h6" gutterBottom>
                      {i18n.t("whatsappModal.instagram.title")}
                    </Typography>
                    {whatsAppId && whatsApp?.instagramUsername ? (
                      <Typography variant="body2" gutterBottom>
                        {i18n.t("whatsappModal.instagram.connectedAccount")} <strong>@{whatsApp.instagramUsername}</strong>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {i18n.t("whatsappModal.instagram.description")}
                      </Typography>
                    )}
                    {!instagramSignupEnabled && (
                      <Typography variant="body2" color="error" gutterBottom>
                        {i18n.t("whatsappModal.instagram.notConfigured")}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleConnectInstagram}
                      disabled={instagramLoginLoading || !instagramSignupEnabled}
                      style={{ background: "linear-gradient(45deg,#833AB4,#E1306C,#F77737)", color: "#fff", marginTop: 8 }}
                    >
                      {instagramLoginLoading
                        ? i18n.t("whatsappModal.instagram.redirecting")
                        : whatsAppId && whatsApp?.instagramUsername
                        ? i18n.t("whatsappModal.instagram.reconnect")
                        : i18n.t("whatsappModal.instagram.connect")}
                    </Button>
                  </Box>
                )}

                {/* ===== CONFIGURAÇÃO DE SMS (apenas se tipo = sms) ===== */}
                {values.type === "sms" && (() => {
                  const smsProvider = values.smsConfig?.provider || "witime";
                  const smsProviderName =
                    smsProvider === "smsbarato"
                      ? "SMS Barato"
                      : smsProvider === "smsdev"
                        ? "SMSDev"
                        : "Witi.me";

                  return (
                  <Box className={classes.cloudApiSection}>
                    <Typography variant="h6" gutterBottom>
                      {i18n.t("whatsappModal.sms.title", { provider: smsProviderName })}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel id="sms-provider-label">{i18n.t("whatsappModal.sms.provider")}</InputLabel>
                          <Field
                            as={Select}
                            labelId="sms-provider-label"
                            label={i18n.t("whatsappModal.sms.provider")}
                            name="smsConfig.provider"
                          >
                            <MenuItem value="witime">{i18n.t("whatsappModal.sms.providers.witime")}</MenuItem>
                            <MenuItem value="smsbarato">{i18n.t("whatsappModal.sms.providers.smsbarato")}</MenuItem>
                            <MenuItem value="smsdev">{i18n.t("whatsappModal.sms.providers.smsdev")}</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={smsProvider === "witime" ? 8 : 6}>
                        <Field
                          as={TextField}
                          label={`${i18n.t("whatsappModal.sms.apiKey")}${values.smsConfig?.hasApiKey ? "" : " *"}`}
                          name="smsConfig.apiKey"
                          type={showSmsApiKey ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder={values.smsConfig?.apiKeyHint || ""}
                          InputLabelProps={values.smsConfig?.hasApiKey ? { shrink: true } : undefined}
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={
                            values.smsConfig?.hasApiKey
                              ? i18n.t("whatsappModal.sms.apiKeyCurrent", { hint: values.smsConfig?.apiKeyHint || "****" })
                              : smsProvider === "witime"
                                ? i18n.t("whatsappModal.sms.apiKeyHelpWitime")
                                : i18n.t("whatsappModal.sms.apiKeyHelp", { provider: smsProviderName })
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={i18n.t("whatsappModal.sms.showHideApiKey")}
                                  onClick={() => setShowSmsApiKey(prev => !prev)}
                                  edge="end"
                                >
                                  {showSmsApiKey ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      {smsProvider === "witime" && (
                        <Grid item xs={12} sm={4}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.sms.route")}
                            name="smsConfig.rota"
                            type="number"
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            helperText={i18n.t("whatsappModal.sms.routeHelp")}
                          />
                        </Grid>
                      )}
                      {smsProvider === "witime" && (
                        <Grid item xs={12} sm={6}>
                          <FormControl variant="outlined" margin="dense" fullWidth>
                            <InputLabel id="sms-tipo-envio-label">{i18n.t("whatsappModal.sms.sendType")}</InputLabel>
                            <Field
                              as={Select}
                              labelId="sms-tipo-envio-label"
                              label={i18n.t("whatsappModal.sms.sendType")}
                              name="smsConfig.tipoEnvio"
                            >
                              <MenuItem value="common">{i18n.t("whatsappModal.sms.sendTypes.common")}</MenuItem>
                              <MenuItem value="token">{i18n.t("whatsappModal.sms.sendTypes.token")}</MenuItem>
                              <MenuItem value="debug">{i18n.t("whatsappModal.sms.sendTypes.debug")}</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.sms.reference")}
                          name="smsConfig.referencia"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={i18n.t("whatsappModal.sms.referenceHelp", { provider: smsProviderName })}
                        />
                      </Grid>
                      {smsProvider === "witime" && whatsAppId && values.smsConfig?.webhookToken && (() => {
                        const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/webhook/witime/${values.smsConfig.webhookToken}`;
                        return (
                        <Grid item xs={12}>
                          <TextField
                            label={i18n.t("whatsappModal.sms.webhookUrl")}
                            value={webhookUrl}
                            type={showWebhookUrl ? "text" : "password"}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={i18n.t("whatsappModal.sms.showHideWebhook")}
                                    onClick={() => setShowWebhookUrl(prev => !prev)}
                                  >
                                    {showWebhookUrl ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                  <IconButton
                                    aria-label={i18n.t("whatsappModal.sms.copyWebhook")}
                                    onClick={() => {
                                      navigator.clipboard
                                        .writeText(webhookUrl)
                                        .then(() => toast.success(i18n.t("whatsappModal.sms.webhookCopied")))
                                        .catch(() => toast.error(i18n.t("whatsappModal.sms.webhookCopyError")));
                                    }}
                                    edge="end"
                                  >
                                    <FileCopy />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            helperText={i18n.t("whatsappModal.sms.webhookHelp")}
                          />
                        </Grid>
                        );
                      })()}
                      {smsProvider === "witime" && !whatsAppId && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("whatsappModal.sms.webhookAfterSave")}
                          </Typography>
                        </Grid>
                      )}
                      {smsProvider === "smsbarato" && (
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.sms.baseUrl")}
                            name="smsConfig.baseUrl"
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            placeholder="https://sistema81.smsbarato.com.br"
                            helperText={i18n.t("whatsappModal.sms.baseUrlHelp")}
                          />
                        </Grid>
                      )}
                      {smsProvider === "smsbarato" && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("whatsappModal.sms.smsbaratoNote")}
                          </Typography>
                        </Grid>
                      )}
                      {smsProvider === "smsdev" && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("whatsappModal.sms.smsdevNote")}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  );
                })()}

                {/* ===== CONFIGURAÇÃO DE EMAIL (apenas se tipo = email) ===== */}
                {values.type === "email" && (
                  <Box className={classes.cloudApiSection}>
                    <Typography variant="h6" gutterBottom>
                      {i18n.t("whatsappModal.email.title")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={`${i18n.t("whatsappModal.email.user")} *`}
                          name="emailConfig.emailUser"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={`${i18n.t("whatsappModal.email.password")}${values.emailConfig?.hasPassword ? "" : " *"}`}
                          name="emailConfig.emailPassword"
                          type={showEmailPassword ? "text" : "password"}
                          autoComplete="new-password"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={
                            values.emailConfig?.hasPassword
                              ? i18n.t("whatsappModal.email.passwordKeep")
                              : undefined
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={i18n.t("whatsappModal.email.showHidePassword")}
                                  onClick={() => setShowEmailPassword(prev => !prev)}
                                  edge="end"
                                >
                                  {showEmailPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.email.fromName")}
                          name="emailConfig.fromName"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" margin="dense">
                          <InputLabel id="email-inbound-protocol-label">{i18n.t("whatsappModal.email.inboundProtocol")}</InputLabel>
                          <Select
                            labelId="email-inbound-protocol-label"
                            label={i18n.t("whatsappModal.email.inboundProtocol")}
                            value={getInboundProtocol(values.emailConfig)}
                            onChange={e => setFieldValue("emailConfig.protocol", e.target.value)}
                          >
                            <MenuItem value="pop3">{i18n.t("whatsappModal.email.pop3Suggested")}</MenuItem>
                            <MenuItem value="imap">IMAP</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={`${i18n.t("whatsappModal.email.host", { protocol: "SMTP" })} *`}
                          name="emailConfig.smtpHost"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.email.port", { protocol: "SMTP" })}
                          name="emailConfig.smtpPort"
                          type="number"
                          fullWidth
                          variant="outlined"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControlLabel
                          control={
                            <Field as={Switch} color="primary" name="emailConfig.smtpSecure" checked={values?.emailConfig?.smtpSecure} />
                          }
                          label="SMTP Secure"
                        />
                      </Grid>
                      {/* Recebimento: só o bloco do protocolo escolhido aparece */}
                      {getInboundProtocol(values.emailConfig) === "pop3" ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              label={`${i18n.t("whatsappModal.email.host", { protocol: "POP3" })} *`}
                              name="emailConfig.pop3Host"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Field
                              as={TextField}
                              label={i18n.t("whatsappModal.email.port", { protocol: "POP3" })}
                              name="emailConfig.pop3Port"
                              type="number"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControlLabel
                              control={
                                <Field as={Switch} color="primary" name="emailConfig.pop3Secure" checked={Boolean(values?.emailConfig?.pop3Secure)} />
                              }
                              label="POP3 Secure (SSL/TLS)"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Field as={Switch} color="primary" name="emailConfig.pop3DeleteAfterFetch" checked={Boolean(values?.emailConfig?.pop3DeleteAfterFetch)} />
                              }
                              label={i18n.t("whatsappModal.email.deleteAfterFetch")}
                            />
                            <Typography variant="caption" display="block" color="textSecondary">
                              {i18n.t("whatsappModal.email.deleteAfterFetchHelp")}
                            </Typography>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              label={`${i18n.t("whatsappModal.email.host", { protocol: "IMAP" })} *`}
                              name="emailConfig.imapHost"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Field
                              as={TextField}
                              label={i18n.t("whatsappModal.email.port", { protocol: "IMAP" })}
                              name="emailConfig.imapPort"
                              type="number"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormControlLabel
                              control={
                                <Field as={Switch} color="primary" name="emailConfig.imapSecure" checked={Boolean(values?.emailConfig?.imapSecure)} />
                              }
                              label="IMAP Secure"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("whatsappModal.email.mailbox")}
                              name="emailConfig.mailbox"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleTestEmailConnection(values)}
                          disabled={testingEmailConnection}
                        >
                          {testingEmailConnection ? <CircularProgress size={20} style={{ marginRight: 8 }} /> : null}
                          {i18n.t("whatsappModal.email.test", { protocol: getInboundProtocol(values.emailConfig).toUpperCase() })}
                        </Button>
                      </Grid>
                      {emailTestResult && (
                        <Grid item xs={12}>
                          {(() => {
                            const inboundResult = emailTestResult?.inbound || emailTestResult?.imap;
                            const inboundResultLabel = String(
                              inboundResult?.protocol || getInboundProtocol(values.emailConfig)
                            ).toUpperCase();
                            const inboundOk = Boolean(inboundResult?.success);
                            const smtpOk = Boolean(emailTestResult?.smtp?.success);
                            const allOk = inboundOk && smtpOk;
                            const partialOk = !allOk && (inboundOk || smtpOk);

                            return (
                          <Box
                            p={2}
                            borderRadius={4}
                            className={allOk ? classes.noticeSuccess : partialOk ? classes.noticeWarning : classes.noticeError}
                          >
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                              {allOk
                                ? i18n.t("whatsappModal.email.result.ok")
                                : partialOk
                                  ? i18n.t("whatsappModal.email.result.partial")
                                  : i18n.t("whatsappModal.email.result.failed")}
                            </Typography>
                            <Typography variant="body2" style={{ marginBottom: 4 }}>
                              <strong>{inboundResultLabel}:</strong> {inboundOk ? "OK" : i18n.t("whatsappModal.email.result.statusFailed")}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ display: "block", whiteSpace: "pre-wrap", marginBottom: 8 }}
                            >
                              {inboundResult?.message || i18n.t("whatsappModal.email.result.noDetails")}
                            </Typography>
                            <Typography variant="body2" style={{ marginBottom: 4 }}>
                              <strong>SMTP:</strong> {emailTestResult?.smtp?.success ? "OK" : i18n.t("whatsappModal.email.result.statusFailed")}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ display: "block", whiteSpace: "pre-wrap" }}
                            >
                              {emailTestResult?.smtp?.message || i18n.t("whatsappModal.email.result.noDetails")}
                            </Typography>
                          </Box>
                            );
                          })()}
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* ===== MENSAGENS AUTOMÁTICAS (somente canais com automação) ===== */}
                {channelSupportsAutomation(values.type) && (
                <>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="greetingMessage"
                    error={
                      touched.greetingMessage && Boolean(errors.greetingMessage)
                    }
                    helperText={
                      touched.greetingMessage && errors.greetingMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.complationMessage")}
                    type="complationMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="complationMessage"
                    error={
                      touched.complationMessage &&
                      Boolean(errors.complationMessage)
                    }
                    helperText={
                      touched.complationMessage && errors.complationMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.outOfHoursMessage")}
                    type="outOfHoursMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="outOfHoursMessage"
                    error={
                      touched.outOfHoursMessage &&
                      Boolean(errors.outOfHoursMessage)
                    }
                    helperText={
                      touched.outOfHoursMessage && errors.outOfHoursMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.ratingMessage")}
                    type="ratingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="ratingMessage"
                    error={
                      touched.ratingMessage && Boolean(errors.ratingMessage)
                    }
                    helperText={touched.ratingMessage && errors.ratingMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                </>
                )}
                <div>
                  {/* O backend nunca devolve o token salvo: só `hasToken` e a dica
                      mascarada. Em branco mantém, valor novo troca, "Remover" apaga. */}
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.token")}
                    type={showConnectionToken ? "text" : "password"}
                    autoComplete="new-password"
                    fullWidth
                    name="token"
                    variant="outlined"
                    margin="dense"
                    disabled={removeToken}
                    placeholder={values.tokenHint || ""}
                    InputLabelProps={values.hasToken ? { shrink: true } : undefined}
                    helperText={
                      removeToken
                        ? i18n.t("whatsappModal.form.tokenWillBeRemoved")
                        : values.hasToken
                          ? i18n.t("whatsappModal.form.tokenCurrent", { hint: values.tokenHint || "****" })
                          : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={i18n.t("whatsappModal.form.showHideToken")}
                            onClick={() => setShowConnectionToken(prev => !prev)}
                            edge="end"
                          >
                            {showConnectionToken ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  {whatsAppId && values.hasToken && (
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setFieldValue("token", "");
                        setRemoveToken(prev => !prev);
                      }}
                    >
                      {removeToken
                        ? i18n.t("whatsappModal.form.undoRemoveToken")
                        : i18n.t("whatsappModal.form.removeToken")}
                    </Button>
                  )}
                </div>
                {channelSupportsAutomation(values.type) ? (
                  <QueueSelect
                    selectedQueueIds={selectedQueueIds}
                    onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                  />
                ) : (
                  // Canais sem menu de filas: permitir vincular no máximo 1 fila.
                  <QueueSelect
                    multiple={false}
                    selectedQueueIds={(selectedQueueIds || [])[0] || null}
                    onChange={(selectedId) =>
                      handleChangeQueue(selectedId ? [selectedId] : [])
                    }
                  />
                )}
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                >
                  <InputLabel>
                    {i18n.t("whatsappModal.form.prompt")}
                  </InputLabel>
                  <Select
                    labelId="dialog-select-prompt-label"
                    id="dialog-select-prompt"
                    name="promptId"
                    value={selectedPrompt || ""}
                    onChange={handleChangePrompt}
                    label={i18n.t("whatsappModal.form.prompt")}
                    fullWidth
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    <MenuItem value={""}>{i18n.t("common.none")}</MenuItem>
                    {prompts.map((prompt) => (
                      <MenuItem
                        key={prompt.id}
                        value={prompt.id}
                      >
                        {prompt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div>
                  <h3>{i18n.t("whatsappModal.form.queueRedirection")}</h3>
                  <p>{i18n.t("whatsappModal.form.queueRedirectionDesc")}</p>
                  <Grid container spacing={2}>
                    <Grid item sm={6} >
                      <Field
                        fullWidth
                        type="number"
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeToTransfer")}
                        name="timeToTransfer"
                        error={touched.timeToTransfer && Boolean(errors.timeToTransfer)}
                        helperText={touched.timeToTransfer && errors.timeToTransfer}
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                        InputLabelProps={{ shrink: values.timeToTransfer ? true : false }}
                      />
                    </Grid>

                    <Grid item sm={6}>
                      <QueueSelect
                        selectedQueueIds={selectedQueueId}
                        onChange={(selectedId) => {
                          setSelectedQueueId(selectedId)
                        }}
                        multiple={false}
                        title={i18n.t("whatsappModal.form.transferQueue")}
                      />
                    </Grid>
                  </Grid>
                  <Grid spacing={2} container>
                    {/* ENCERRAR CHATS ABERTOS APÓS X HORAS */}
                    <Grid xs={12} md={12} item>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicket")}
                        fullWidth
                        name="expiresTicket"
                        variant="outlined"
                        margin="dense"
                        error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                        helperText={touched.expiresTicket && errors.expiresTicket}
                      />
                    </Grid>
                  </Grid>
                  {/* MENSAGEM POR INATIVIDADE*/}
                  <div>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                      multiline
                      rows={4}
                      fullWidth
                      name="expiresInactiveMessage"
                      error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                      helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                      variant="outlined"
                      margin="dense"
                    />
                  </div>
                </div>

                {/* ========== NOVA SEÇÃO: CONFIGURAÇÃO DE KANBAN ========== */}
                <Box mt={3}>
                  <Divider />
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whatsappModal.kanban.title")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {i18n.t("whatsappModal.kanban.description")}
                  </Typography>

                  <Box className={classes.kanbanSection}>
                    <Grid container spacing={2}>
                      {/* Switch para habilitar/desabilitar */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Field
                              as={Switch}
                              color="primary"
                              name="kanbanAutoCreate"
                              checked={values.kanbanAutoCreate}
                              onChange={(e) => {
                                setFieldValue("kanbanAutoCreate", e.target.checked);
                                if (!e.target.checked) {
                                  setFieldValue("kanbanBoardId", null);
                                  setFieldValue("kanbanColumnId", null);
                                  setSelectedBoard(null);
                                  setKanbanColumns([]);
                                }
                              }}
                            />
                          }
                          label={i18n.t("whatsappModal.kanban.enable")}
                        />
                      </Grid>

                      {/* Seleção de Board */}
                      {values.kanbanAutoCreate && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined" margin="dense">
                              <InputLabel>{`${i18n.t("whatsappModal.kanban.board")} *`}</InputLabel>
                              <Select
                                name="kanbanBoardId"
                                value={values.kanbanBoardId ?? ""}
                                onChange={(e) => handleBoardChange(e, setFieldValue)}
                                label={`${i18n.t("whatsappModal.kanban.board")} *`}
                              >
                                <MenuItem value="">
                                  <em>{i18n.t("whatsappModal.kanban.boardPlaceholder")}</em>
                                </MenuItem>
                                {kanbanBoards.map((board) => (
                                  <MenuItem key={board.id} value={board.id}>
                                    {board.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="textSecondary">
                              {i18n.t("whatsappModal.kanban.boardHelp")}
                            </Typography>
                          </Grid>

                          {/* Seleção de Coluna */}
                          <Grid item xs={12} sm={6}>
                            <FormControl
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              disabled={!values.kanbanBoardId}
                            >
                              <InputLabel>{`${i18n.t("whatsappModal.kanban.column")} *`}</InputLabel>
                              <Select
                                name="kanbanColumnId"
                                value={values.kanbanColumnId ?? ""}
                                onChange={(e) => setFieldValue("kanbanColumnId", e.target.value)}
                                label={`${i18n.t("whatsappModal.kanban.column")} *`}
                              >
                                <MenuItem value="">
                                  <em>{i18n.t("whatsappModal.kanban.columnPlaceholder")}</em>
                                </MenuItem>
                                {kanbanColumns
                                  .sort((a, b) => a.order - b.order)
                                  .map((column) => (
                                    <MenuItem key={column.id} value={column.id}>
                                      {column.name}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="textSecondary">
                              {i18n.t("whatsappModal.kanban.columnHelp")}
                            </Typography>
                          </Grid>

                          {/* Seleção de Usuário (opcional) */}
                          <Grid item xs={12} sm={6}>
                            <FormControl
                              fullWidth
                              variant="outlined"
                              margin="dense"
                            >
                              <InputLabel>{i18n.t("whatsappModal.kanban.user")}</InputLabel>
                              <Select
                                name="userCardsId"
                                value={values.userCardsId ?? ""}
                                onChange={(e) => setFieldValue("userCardsId", e.target.value)}
                                label={i18n.t("whatsappModal.kanban.user")}
                              >
                                <MenuItem value="">
                                  <em>{i18n.t("common.none")}</em>
                                </MenuItem>
                                {users.map((user) => (
                                  <MenuItem key={user.id} value={user.id}>
                                    {user.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Typography variant="caption" color="textSecondary">
                              {i18n.t("whatsappModal.kanban.userHelp")}
                            </Typography>
                          </Grid>

                          {/* Informação importante */}
                          <Grid item xs={12}>
                            <Box
                              p={2}
                              borderRadius={1}
                              className={classes.noticeWarning}
                            >
                              <Typography variant="body2">
                                <strong>{i18n.t("whatsappModal.kanban.prerequisiteLabel")}</strong>{" "}
                                {i18n.t("whatsappModal.kanban.prerequisiteText")}
                              </Typography>
                            </Box>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Box>
                {/* ========== FIM DA NOVA SEÇÃO ========== */}

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
